import { useState } from 'react';
import { Star } from 'lucide-react';
import { Button, Input } from '@dreamtree-org/twreact-ui';

/**
 * Renders a paused ask_user question (services/chat/tools/index.js, chatEngine.js's
 * executeLoop() pause/resumeTurn()) — the agent's set-of-options + optional free-text
 * interaction. Shown in place of the "thinking…" indicator while
 * pendingJob.status === 'waiting_for_user'; submitting calls answerQuestion() and the
 * panel goes back to the normal polling/"thinking" state until the turn finishes.
 */
export default function AskUserCard({ question, onAnswer }) {
  const [freeText, setFreeText] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const choose = async (value) => {
    if (submitting) return;
    setSubmitting(true);
    await onAnswer({ selectedValue: value });
  };

  const submitFreeText = async (e) => {
    e.preventDefault();
    if (!freeText.trim() || submitting) return;
    setSubmitting(true);
    await onAnswer({ freeText: freeText.trim() });
  };

  return (
    <div className="max-w-[90%] rounded-lg border border-[var(--app-border)] bg-[var(--app-surface)] p-3 space-y-3">
      <p className="text-sm font-medium">{question.question}</p>
      <div className="flex flex-col gap-2">
        {question.options.map((opt) => {
          const isRecommended = question.recommended === opt.value;
          return (
            <button
              key={opt.value}
              type="button"
              disabled={submitting}
              onClick={() => choose(opt.value)}
              className={`text-left px-3 py-2 rounded-md border text-sm flex items-start gap-2 disabled:opacity-50 ${
                isRecommended ? 'border-primary-500 bg-primary-50' : 'border-[var(--app-border)] hover:bg-[var(--app-border)]/20'
              }`}
            >
              {isRecommended && <Star size={14} className="text-primary-600 shrink-0 mt-0.5" fill="currentColor" />}
              <span>
                <span className="font-medium">{opt.label}</span>
                {opt.description && <span className="block text-xs text-secondary-500">{opt.description}</span>}
              </span>
            </button>
          );
        })}
      </div>
      {question.allowFreeText && (
        <form onSubmit={submitFreeText} className="flex gap-2">
          <Input
            value={freeText}
            onChange={(e) => setFreeText(e.target.value)}
            placeholder="Or type your own answer…"
            className="flex-1"
            disabled={submitting}
          />
          <Button type="submit" size="sm" disabled={submitting || !freeText.trim()}>Send</Button>
        </form>
      )}
    </div>
  );
}
